import React from 'react';

import { AlertTriangle } from 'lucide-react';

import {
	findViolations,
	formatLimitValue,
	LIMITED_RATES,
	useGetFilePricingLimitsQuery,
} from '../api/pricingApprovals';
import { useFeature } from '../hooks';

import type {
	FilePricingLimits,
	LimitedRate,
	LimitViolation,
} from '../api/pricingApprovals';

const num = (v: unknown): number | null => {
	const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
	return Number.isFinite(n) ? n : null;
};

// The values the Customize Fee Structure form proposes, by limited rate.
export const proposedFromForm = (
	values: any,
): Record<LimitedRate, number[]> => {
	const standard = values?.rates?.standard_rates;
	// In same_for_all mode the form edits VISA's row and submits only that one.
	const byCard = (
		standard?.mode === 'same_for_all'
			? [standard?.by_card_type?.VISA]
			: Object.values(standard?.by_card_type ?? {})
	) as any[];
	const pick = (key: string) =>
		byCard.map((r) => num(r?.[key])).filter((n): n is number => n !== null);
	const fees = values?.monthly_and_batch_fees;
	const monthly = (fees?.monthly_fees ?? []) as { amount: unknown }[];
	const batch = num(fees?.batch_fee);
	return {
		discount_rate: pick('discount_rate'),
		authorization_fee: pick('authorization_fee'),
		monthly_fee: monthly.length
			? [monthly.reduce((sum, f) => sum + (num(f.amount) ?? 0), 0)]
			: [],
		batch_fee: batch === null ? [] : [batch],
	};
};

const LABEL: Record<LimitedRate, string> = {
	discount_rate: 'Discount rate',
	authorization_fee: 'Authorization fee',
	monthly_fee: 'Monthly service fee',
	batch_fee: 'Batch fee',
};

export interface QuoteLimits {
	data: FilePricingLimits;
	violations: LimitViolation[];
}

// The template limits for a statement and what the form breaks (v9 s-quote renderQuote).
export const useQuoteLimits = (
	fileId: string | undefined,
	values: any,
): QuoteLimits | null => {
	const enabled = useFeature('approvals');
	const { data } = useGetFilePricingLimitsQuery(fileId ?? '', {
		skip: !enabled || !fileId,
	});
	if (!enabled || !fileId || !data?.limits) return null;
	return {
		data,
		violations: findViolations(
			data.limits,
			proposedFromForm(values),
			data.can_approve,
		),
	};
};

// Header pill: "Within limits" or "Needs manager approval".
export const QuoteLimitsStatus: React.FC<{ limits: QuoteLimits }> = ({
	limits,
}) => {
	const out = limits.violations.length > 0;
	const pill = (
		<span
			className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-1 text-xs font-medium leading-[1.3] ${
				out
					? 'border-red-100 bg-red-50 text-red-700'
					: 'border-green-200 bg-green-100 text-green-800'
			}`}
		>
			{out ? 'Needs manager approval' : 'Within limits'}
		</span>
	);
	if (!limits.data.pending_approval) return pill;
	return (
		<span className="inline-flex items-center gap-2">
			<span className="inline-flex items-center whitespace-nowrap rounded-full border border-sand-300 bg-sand-100 px-2 py-1 text-xs font-medium leading-[1.3] text-sand-900">
				Approval pending
			</span>
			{pill}
		</span>
	);
};

// v9 #qBanner, plus each limited rate's floor and ceiling (the slider scale in v9).
const QuoteLimitsPanel: React.FC<{
	limits: QuoteLimits;
	reason: string;
	onReason: (reason: string) => void;
}> = ({ limits, reason, onReason }) => {
	const { data, violations } = limits;
	const block = data.action === 'block';
	const rangeLabel = data.can_approve ? 'template range' : 'rep range';
	const rows = LIMITED_RATES.filter(({ rate }) => data.limits?.[rate]);
	return (
		<div className="space-y-3 px-6 pt-6">
			{violations.length > 0 && (
				<div
					role="alert"
					className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-700"
				>
					<AlertTriangle className="mt-px h-5 w-5 flex-none text-red-500" />
					<div>
						<b className="font-medium">
							{block
								? 'This quote breaks a limit.'
								: 'This quote needs a manager.'}
						</b>{' '}
						{violations.map((v, i) => (
							<React.Fragment key={v.rate}>
								{i > 0 && '. '}
								<b className="font-medium">{LABEL[v.rate]}</b>{' '}
								{formatLimitValue(v.rate, v.proposed)} is outside the{' '}
								{v.kind === 'outside_rep_range' ? rangeLabel : 'template range'}{' '}
								({formatLimitValue(v.rate, v.limit)})
							</React.Fragment>
						))}
						.{' '}
						{block
							? 'Your organization blocks the save. Move each value inside its limits, then save.'
							: 'Save sends it to the approval queue with these reasons.'}
					</div>
				</div>
			)}
			{violations.length > 0 && !block && (
				<label className="block text-[13px] text-gray-700">
					Reason for the approver
					<textarea
						rows={2}
						maxLength={2000}
						value={reason}
						onChange={(e) => onReason(e.target.value)}
						placeholder="For example: matching a competitor offer at 0.24%"
						className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
					/>
				</label>
			)}
			<div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
				{rows.map(({ rate }) => {
					const l = data.limits?.[rate];
					return (
						<span key={rate}>
							{LABEL[rate]}:{' '}
							{l?.rep_min != null && l?.rep_max != null
								? `rep range ${formatLimitValue(rate, l.rep_min)} – ${formatLimitValue(rate, l.rep_max)} · `
								: ''}
							{l?.floor != null
								? `floor ${formatLimitValue(rate, l.floor)}`
								: ''}
							{l?.floor != null && l?.ceiling != null ? ' · ' : ''}
							{l?.ceiling != null
								? `ceiling ${formatLimitValue(rate, l.ceiling)}`
								: ''}
						</span>
					);
				})}
			</div>
		</div>
	);
};

export default QuoteLimitsPanel;
